import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  ArrowRight, Swords, Trophy, Users, Timer, Zap, Target, Star,
  Crown, Medal, Flame, Brain, TrendingUp, BarChart3
} from 'lucide-react';

const roundTypes = [
  {
    name: 'Speed Round',
    icon: Zap,
    color: 'bg-red-500',
    duration: '5 min',
    description: '30 rapid-fire problems in 5 minutes. Pure speed and accuracy. Individual scoring — compete against your own best time and the class leaderboard.',
    skills: ['Mental Math', 'Pattern Recognition', 'Quick Recall'],
  },
  {
    name: 'Target Round',
    icon: Target,
    color: 'bg-blue-500',
    duration: '8 min',
    description: '4 pairs of problems, 2 minutes each. Problems build on each other — the answer to problem 1 feeds into problem 2. Strategic thinking under time pressure.',
    skills: ['Multi-step Problems', 'Strategic Thinking', 'Accuracy'],
  },
  {
    name: 'Team Battle',
    icon: Users,
    color: 'bg-green-500',
    duration: '10 min',
    description: 'Teams of 3-4 compete collaboratively. Problems require discussion and delegation — who solves which part? Builds communication and mathematical teamwork.',
    skills: ['Collaboration', 'Division of Labor', 'Communication'],
  },
  {
    name: 'Countdown Elimination',
    icon: Crown,
    color: 'bg-purple-500',
    duration: '15 min',
    description: 'Head-to-head bracket-style elimination. Two students face off on a problem — first correct answer advances. The crowd goes wild! Final two compete for Champion.',
    skills: ['Pressure Performance', 'Speed Under Stress', 'Competition Readiness'],
  },
  {
    name: 'Puzzle Challenge',
    icon: Brain,
    color: 'bg-amber-500',
    duration: '12 min',
    description: 'Logic puzzles, brain teasers, and non-standard problems that reward creative thinking over speed. Bonus points for elegant solutions and unique approaches.',
    skills: ['Creative Thinking', 'Logical Reasoning', 'Out-of-Box Solutions'],
  },
];

const leaderboard = [
  { rank: 1, name: 'Sofia G.', points: 2840, streak: 12, badge: '🏆' },
  { rank: 2, name: 'Ryan K.', points: 2650, streak: 8, badge: '🥈' },
  { rank: 3, name: 'Aanya P.', points: 2420, streak: 10, badge: '🥉' },
  { rank: 4, name: 'Ethan W.', points: 2180, streak: 6, badge: '⭐' },
  { rank: 5, name: 'Lily W.', points: 1950, streak: 7, badge: '⭐' },
];

export default function MathArenaPage() {
  return (
    <div data-testid="math-arena-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 overflow-hidden bg-gradient-to-br from-red-900 via-orange-900 to-amber-900">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-10 left-10 text-[200px] font-black text-white/5">⚔️</div>
          <div className="absolute bottom-10 right-10 text-[150px] font-black text-white/5">🏆</div>
        </div>
        <div className="container-app relative z-10">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/15 text-amber-300 text-sm font-bold mb-6 animate-pulse">
              <Flame className="w-4 h-4" /> Live Competition Every Class Session
            </div>
            <h1 className="font-heading text-4xl md:text-6xl font-extrabold text-white mb-6">
              Math Arena
            </h1>
            <p className="text-2xl text-amber-200 font-bold mb-4">
              Where Math Gets Competitive. And Fun.
            </p>
            <p className="text-xl text-white/80 mb-8">
              Head-to-head battles. Speed rounds. Team challenges. Countdown elimination.
              Every Ready Set Math class ends with an Arena session that turns problem-solving
              into the most exciting part of the week.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/math">
                <Button className="bg-amber-400 text-amber-900 hover:bg-amber-300 text-lg px-8 py-6 font-bold rounded-full">
                  Join Math Arena <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
              <a href="#rounds">
                <Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">
                  See Round Types
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-10 bg-white border-b">
        <div className="container-app">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-8 text-center">
            {[
              { step: '1', label: 'Learn', desc: 'First 40 min: New concepts with hands-on manipulatives' },
              { step: '2', label: 'Practice', desc: 'Next 20 min: Guided practice with increasing difficulty' },
              { step: '3', label: 'Arena!', desc: 'Final 20 min: Competitive rounds with points & prizes' },
              { step: '4', label: 'Rank Up', desc: 'Points hit the leaderboard. Climb the ranks weekly!' },
            ].map((s, i) => (
              <div key={i}>
                <div className={`w-12 h-12 rounded-full ${i === 2 ? 'bg-red-500 animate-pulse' : 'bg-rsc-navy'} text-white text-xl font-bold flex items-center justify-center mx-auto mb-3`}>
                  {s.step}
                </div>
                <p className="font-bold text-rsc-navy">{s.label}</p>
                <p className="text-sm text-gray-600">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Round Types */}
      <section id="rounds" className="section-padding bg-gray-50">
        <div className="container-app">
          <div className="text-center mb-16">
            <h2 className="font-heading text-3xl md:text-4xl font-bold text-rsc-navy mb-4">5 Arena Round Types</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">Every session rotates through different round types to build diverse mathematical skills.</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {roundTypes.map((round, i) => {
              const Icon = round.icon;
              return (
                <Card key={i} className="card-marketing h-full overflow-hidden">
                  <div className={`h-1.5 ${round.color}`} />
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-3">
                        <div className={`w-10 h-10 rounded-xl ${round.color} flex items-center justify-center`}>
                          <Icon className="w-5 h-5 text-white" />
                        </div>
                        <h3 className="font-heading text-lg font-bold text-rsc-navy">{round.name}</h3>
                      </div>
                      <Badge variant="outline" className="text-xs"><Timer className="w-3 h-3 mr-1" />{round.duration}</Badge>
                    </div>
                    <p className="text-gray-700 text-sm mb-4">{round.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {round.skills.map((s, si) => (
                        <Badge key={si} className="bg-gray-100 text-gray-600 text-xs">{s}</Badge>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Sample Leaderboard */}
      <section className="section-padding bg-gradient-to-br from-rsc-navy to-indigo-900 text-white">
        <div className="container-app">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Weekly Leaderboard</h2>
              <p className="text-white/70 text-lg mb-6">
                Points from every Arena session accumulate on the weekly leaderboard.
                Top performers earn bonus rewards, special badges, and bragging rights.
                Leaderboards reset monthly so everyone gets a fresh start.
              </p>
              <div className="space-y-4">
                {[
                  { icon: Trophy, label: 'Season Champion', desc: 'Highest total points at end of semester — wins the Golden Calculator trophy' },
                  { icon: Flame, label: 'Streak Master', desc: 'Longest winning streak — consecutive Arena session victories' },
                  { icon: Star, label: 'Most Improved', desc: 'Biggest score improvement over the semester — growth matters most' },
                ].map((award, i) => {
                  const Icon = award.icon;
                  return (
                    <div key={i} className="flex items-start gap-3">
                      <Icon className="w-6 h-6 text-amber-400 flex-shrink-0" />
                      <div>
                        <p className="font-bold text-white">{award.label}</p>
                        <p className="text-sm text-white/60">{award.desc}</p>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <Card className="bg-white/5 border-white/10">
              <CardContent className="p-6">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-heading text-xl font-bold text-white flex items-center gap-2">
                    <BarChart3 className="w-5 h-5 text-amber-400" /> This Week's Standings
                  </h3>
                  <Badge className="bg-amber-500 text-white text-xs">Live</Badge>
                </div>
                <div className="space-y-3">
                  {leaderboard.map((entry) => (
                    <div key={entry.rank} className={`flex items-center gap-4 p-3 rounded-lg ${entry.rank <= 3 ? 'bg-white/10' : 'bg-white/5'}`}>
                      <span className="text-2xl w-8 text-center">{entry.badge}</span>
                      <div className="flex-1">
                        <p className="font-bold text-white">{entry.name}</p>
                        <p className="text-xs text-white/50">{entry.streak}-session streak 🔥</p>
                      </div>
                      <div className="text-right">
                        <p className="font-black text-amber-400 text-lg">{entry.points.toLocaleString()}</p>
                        <p className="text-xs text-white/50">pts</p>
                      </div>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-gradient-to-r from-red-600 to-amber-600 text-white">
        <div className="container-app text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-6">Ready to Enter the Arena?</h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">Math Arena is included in every Ready Set Math course. No extra cost. Just competitive, hands-on math fun.</p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link to="/math"><Button className="bg-white text-red-700 hover:bg-gray-100 text-lg px-8 py-6 font-bold rounded-full">Join Ready Set Math <ArrowRight className="ml-2 h-5 w-5" /></Button></Link>
            <Link to="/contact"><Button variant="outline" className="text-lg px-8 py-6 text-white border-white hover:bg-white/10">Book Free Trial</Button></Link>
          </div>
        </div>
      </section>
    </div>
  );
}
